import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { useMutation, useQuery } from "@apollo/client";
import moment from "moment";

import {
ChainedOfferEventStatus,
DeleteMarathonEventMutation,
DeleteMarathonEventMutationVariables,
GetAllMarathonEventsQuery,
GetAllMarathonMilestoneConfigsQuery,
GetMarathonEventQuery,
GetMarathonEventQueryVariables,
UpdateMarathonEventMutation,
UpdateMarathonEventMutationVariables,
GetAllCategoriesForMarathonQuery
} from "../../../../../../__gqltypes__";
import { eventMatcherDefaultConfig, EventMatchers, EventMatchersConfig, userIdsArrayToString, userIdsStringToArray } from "../../../general/EventMatchers";
import { EventBaseData, EventBaseDataConfig, eventBaseDataConfigDefault } from "../../../general/EventBaseData";
import { DeleteModal } from "../../../general/DeleteModal";
import { DELETE_MARATHON_EVENT, GET_ALL_MARATHON_MILESTONE_TEMPLATE, GET_MARATHON_EVENT, UPDATE_MARATHON_EVENT } from "../graphql";
import { LoadingLogo } from "../../../../devtools/components/Modal";
import {
  DetectedLiveChanges,
  detectLiveEventChanges,
  economyConfigToRuntime,
  economyToInput,
  formatEventDate,
  formatEventDuration,
  groupConfigToRuntime,
  groupsToInput,
  hasLiveChanges,
  highestChangeRisk,
  MARATHON_ECONOMY_DEFAULTS,
  MARATHON_MIN_APP_VERSION,
  MarathonEditSnapshot,
  MEconomy,
  MGroup,
  preciseRelativeHint,
  validateMarathonEvent,
} from "../utils";
import { MarathonGroupsData } from "./MarathonGroupsData";
import { MarathonEconomyData } from "./MarathonEconomyData";
import { MarathonTemplatesData } from "./MarathonTemplatesData";

type MarathonEventConfig = GetAllMarathonEventsQuery['liveOps']['getAllMarathonEvents'][0];
type MarathonCategoryData = GetAllCategoriesForMarathonQuery['liveOps']['getAllCategoriesForMarathon'];

// Stable signature of the audience matchers (incl. min app version) for change detection.
function matcherSignature(cfg: EventMatchersConfig): string {
  return JSON.stringify({
    appVersion: cfg.filterAppVersion ?? null,
    tier: cfg.filterSubscriptionTier ?? null,
    userIds: (cfg.filterUserIds ?? '').trim(),
    level: cfg.filterUserLevel ?? null,
    minDate: cfg.filterUserMinCreationDate ? new Date(cfg.filterUserMinCreationDate).getTime() : null,
    maxDate: cfg.filterUserMaxCreationDate ? new Date(cfg.filterUserMaxCreationDate).getTime() : null,
  });
}

type Props = {
  eventId: string;
  onUpserEvent: (value: MarathonEventConfig) => void;
  onRemoveEvent: (eventId: string) => void;
  categoryData: MarathonCategoryData,
};

export function MarathonEventData({eventId, onRemoveEvent, onUpserEvent, categoryData}: Props): JSX.Element {

  const {data: eventData, loading: eventDataLoading, refetch} = useQuery<
    GetMarathonEventQuery,
    GetMarathonEventQueryVariables
  >(GET_MARATHON_EVENT, {
      variables: {
        id: eventId,
      },
      fetchPolicy: 'no-cache',
    }
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [baseData, setBaseData] = useState<EventBaseDataConfig>(eventBaseDataConfigDefault);
  const [filterConfig, setFilterConfig] = useState<EventMatchersConfig>(eventMatcherDefaultConfig);
  const [category, setCategory] = useState('rock');
  const [groups, setGroups] = useState<MGroup[]>([]);
  const [economy, setEconomy] = useState<MEconomy>(MARATHON_ECONOMY_DEFAULTS);

  const {
    data: templateData,
    loading: templatesLoading,
    refetch: refetchTemplates,
  } = useQuery<GetAllMarathonMilestoneConfigsQuery>(GET_ALL_MARATHON_MILESTONE_TEMPLATE);
  const templates = templateData?.liveOps?.getAllMarathonMilestoneConfigs ?? [];

  useEffect(() => {
    if (eventData && eventData.liveOps.getMarathonEvent) {
      const mEvent = eventData.liveOps.getMarathonEvent;
      setBaseData({
        name: mEvent.name,
        startDate: mEvent.startDate,
        stopDate: mEvent.stopDate,
        status: mEvent.status,
      });
      setFilterConfig({
        // OLD events predate the min-version filter and the backend doesn't normalize it onto them,
        // so default it here: pre-release clients can't parse the new event shape and must be filtered
        // out. NEW events already carry it from the backend (no-op here). An explicit existing value
        // (even a different one) is preserved.
        filterAppVersion: mEvent.filterAppVersion ?? MARATHON_MIN_APP_VERSION,
        filterSubscriptionTier: mEvent.filterSubscriptionTier ?? undefined,
        filterUserIds: userIdsArrayToString(mEvent.filterUserIds ?? []),
        filterUserLevel: mEvent.filterUserLevel ?? undefined,
        filterUserMinCreationDate: mEvent.filterUserMinCreationDate ?? undefined,
        filterUserMaxCreationDate: mEvent.filterUserMaxCreationDate ?? undefined,
      });
      setCategory(mEvent.category ?? 'rock');
      setGroups(groupConfigToRuntime(mEvent.milestoneGroups));
      setEconomy(economyConfigToRuntime(mEvent.economy));
    }
  }, [eventData]);

  const categoryOptions = useMemo(() => {
    return categoryData.map((cd) => {
        const v = `${cd.name} [Packs: ${cd.contentPacks}] [Songs: ${cd.songs}]`;
        return (
          <option key={cd.id} value={cd.id}>
            {v}
          </option>
        );
      });
  }, [categoryData]);

  const handleChangeCategory = useCallback((event) => {
    setCategory(event.currentTarget.value);
  }, []);

  const handleUpdateEconomy = useCallback((patch: Partial<MEconomy>) => {
    setEconomy((prev) => ({...prev, ...patch}));
  }, []);

  const [updateEvent, {loading: updateLoading}] = useMutation<
    UpdateMarathonEventMutation,
    UpdateMarathonEventMutationVariables
  >(UPDATE_MARATHON_EVENT,
    {
      onCompleted: ({updateMarathonEvent}) => {
        const updated = updateMarathonEvent.event;
        onUpserEvent(updated);
        refetch();
      },
      onError: (error) => {
        console.log(error);
      },
    }
  );

  const [deleteEvent, { loading: deleteLoading }] = useMutation<
    DeleteMarathonEventMutation,
    DeleteMarathonEventMutationVariables
   >(DELETE_MARATHON_EVENT,
      {
        onCompleted: ({deleteMarathonEvent}) => {
          if (deleteMarathonEvent) {
            onRemoveEvent(eventId);
          } else {
            alert("Error: couldn't remove the current Marathon event");
          }
        },
        onError: (error) => {
          console.log(error);
        },
      }
   );

   const handleDelete = useCallback(() => {
    setShowDeleteModal(false);
    deleteEvent({
      variables: {
        id: eventId,
      }
    });
  }, [deleteEvent, eventId]);

  const validationErrors = useMemo(() => validateMarathonEvent(groups, economy), [groups, economy]);

  // Event window is derived: first group's unlock -> last group's stop
  const derivedWindow = useMemo(() => {
    if (groups.length === 0) {
      return null;
    }
    return {
      startDate: groups[0].unlockDate,
      stopDate: groups[groups.length - 1].stopDate,
    };
  }, [groups]);

  // Live = ACTIVE and inside the derived window (players are mid-event right now).
  const isLive = useMemo(() => {
    if (baseData.status !== ChainedOfferEventStatus.ACTIVE || !derivedWindow) {
      return false;
    }
    const now = moment();
    return moment(derivedWindow.startDate).isSameOrBefore(now) && now.isBefore(moment(derivedWindow.stopDate));
  }, [baseData.status, derivedWindow]);

  // Snapshot of the originally loaded event, in the editor's runtime shapes, for change detection.
  const loadedSnapshot = useMemo<MarathonEditSnapshot | null>(() => {
    const e = eventData?.liveOps?.getMarathonEvent;
    if (!e) {
      return null;
    }
    return {
      name: e.name,
      category: e.category ?? 'rock',
      groups: groupConfigToRuntime(e.milestoneGroups),
      economy: economyConfigToRuntime(e.economy),
      // mirror the min-version default applied on load so old events don't show a spurious matcher change
      matcherSignature: matcherSignature({
        filterAppVersion: e.filterAppVersion ?? MARATHON_MIN_APP_VERSION,
        filterSubscriptionTier: e.filterSubscriptionTier ?? undefined,
        filterUserIds: userIdsArrayToString(e.filterUserIds ?? []),
        filterUserLevel: e.filterUserLevel ?? undefined,
        filterUserMinCreationDate: e.filterUserMinCreationDate ?? undefined,
        filterUserMaxCreationDate: e.filterUserMaxCreationDate ?? undefined,
      }),
    };
  }, [eventData]);

  const [pendingConfirm, setPendingConfirm] = useState<DetectedLiveChanges | null>(null);

  const performSave = useCallback(() => {
    updateEvent({
      variables: {
        input: {
          id: eventId,
          name: baseData.name,
          status: baseData.status,
          category,
          milestoneGroups: groupsToInput(groups),
          economy: economyToInput(economy),
          ...filterConfig,
          filterUserIds: userIdsStringToArray(filterConfig.filterUserIds ?? ''),
        }
      }
    });
  }, [updateEvent, eventId, baseData, category, groups, economy, filterConfig]);

  const handleSave = () => {
    // live events: confirm first, naming which risk categories changed vs the loaded event
    if (isLive && loadedSnapshot) {
      const changes = detectLiveEventChanges(loadedSnapshot, {
        name: baseData.name,
        category,
        groups,
        economy,
        matcherSignature: matcherSignature(filterConfig),
      });
      setPendingConfirm(changes);
      return;
    }
    performSave();
  };

  const confirmSave = () => {
    setPendingConfirm(null);
    performSave();
  };

  const pendingRisk = pendingConfirm ? highestChangeRisk(pendingConfirm) : null;

  if (eventDataLoading || updateLoading || deleteLoading) {
    return (<LoadingLogo show />);
  }

  return (<>
    <Container className="pb-4">
      {isLive && (
        <Alert variant="danger">
          <Alert.Heading as="h6">
            <i className="fas fa-exclamation-triangle mr-2" />
            This event is LIVE — players are mid-event
          </Alert.Heading>
          <p className="mb-0">
            Changes to milestones, groups, or economy can corrupt player progress and already-claimed
            rewards. For structural changes, prefer ending this event and creating a new one.
          </p>
        </Alert>
      )}
      <EventBaseData
        eventId={eventId}
        data={baseData}
        hideDates
        canSave={validationErrors.length === 0}
        onUpdate={setBaseData}
        onShowDeleteModal={() => setShowDeleteModal(true)}
        onSave={handleSave}
        />
      <Row className="mb-3">
        <Col md={3}>Event window (derived):</Col>
        <Col md={9}>
          {derivedWindow ? (
            <>
              {formatEventDate(derivedWindow.startDate)} → {formatEventDate(derivedWindow.stopDate)}
              <div className="text-muted">
                (starts {preciseRelativeHint(derivedWindow.startDate)}, ends{' '}
                {preciseRelativeHint(derivedWindow.stopDate)})
              </div>
            </>
          ) : (
            <span className="text-muted">No groups configured yet.</span>
          )}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={3}>Event duration:</Col>
        <Col md={9}>
          {derivedWindow ? (
            formatEventDuration(derivedWindow.startDate, derivedWindow.stopDate)
          ) : (
            <span className="text-muted">—</span>
          )}
        </Col>
      </Row>
      <EventMatchers config={filterConfig} onUpdate={setFilterConfig} highlight />
      <Row className="mb-3">
        <Col md={3}>Category:</Col>
        <Col md={4}>
          <Form.Group controlId="sale-event-status">
            <Form.Control as="select" type="text" value={category} onChange={handleChangeCategory}>
              {categoryOptions}
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>
      <MarathonEconomyData economy={economy} onUpdate={handleUpdateEconomy}/>
      {validationErrors.length > 0 && (
        <Alert variant="danger">
          <Alert.Heading as="h6">Fix the following before saving:</Alert.Heading>
          <ul className="mb-0">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
      <MarathonGroupsData groups={groups} templates={templates} onUpdateGroups={setGroups}/>
      <MarathonTemplatesData
        templates={templates}
        templatesLoading={templatesLoading}
        onTemplatesChanged={refetchTemplates}
        />
      <DeleteModal
        title="Marathon Event"
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        onDelete={handleDelete}/>
      <Modal show={pendingConfirm !== null} onHide={() => setPendingConfirm(null)}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            {pendingRisk === 'HIGH' ? 'Risky change to a LIVE event' : 'Confirm change to a live event'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingRisk === 'HIGH' && (
            <Alert variant="danger">
              This marathon is <strong>live with players mid-event</strong>. The changes below can{' '}
              <strong>corrupt player progress and already-claimed rewards</strong>. Prefer ending this
              event and creating a new one for structural changes.
            </Alert>
          )}
          {pendingRisk === 'MEDIUM' && (
            <Alert variant="warning">
              This marathon is live. The economy change below affects players currently in the event.
            </Alert>
          )}
          {(pendingRisk === 'LOW' || pendingRisk === null) && (
            <Alert variant="info">This marathon is live. Please confirm the change before saving.</Alert>
          )}
          {pendingConfirm && hasLiveChanges(pendingConfirm) ? (
            <>
              {pendingConfirm.high.length > 0 && (
                <>
                  <div className="font-weight-bold text-danger">High risk — progress &amp; claimed rewards</div>
                  <ul>
                    {pendingConfirm.high.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
              {pendingConfirm.medium.length > 0 && (
                <>
                  <div className="font-weight-bold text-warning">Medium risk — economy</div>
                  <ul>
                    {pendingConfirm.medium.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
              {pendingConfirm.low.length > 0 && (
                <>
                  <div className="font-weight-bold text-muted">Low risk — general info</div>
                  <ul>
                    {pendingConfirm.low.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <p className="text-muted mb-0">No content changes detected — you are re-saving a live event.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant={pendingRisk === 'HIGH' ? 'danger' : pendingRisk === 'MEDIUM' ? 'warning' : 'primary'}
            onClick={confirmSave}
          >
            {pendingRisk === 'HIGH' ? 'Save anyway' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  </>);
}
